import { Store } from 'pullstate'
import { notifications } from '@mantine/notifications'
import {
  Dashboard,
  DEFAULT_ROTATION_MS,
  GRID_COLS,
  GRID_ROWS,
  Slide,
  WidgetInstance,
  WidgetType,
} from './types'
import { getDashboardWidget, hasDashboardWidget } from './widgets/registry'

export const REFERENCE_WIDTH = 1920
export const REFERENCE_HEIGHT = 1080

interface DashboardEditorState {
  dashboard: Dashboard
  competitionId: number | null
  activeSlideIndex: number
  selectedWidgetId: string | null
  scaleFactor: number
  nextId: number
}

export const DashboardEditorStore = new Store<DashboardEditorState>({
  dashboard: {
    slides: [{ id: 's1', widgets: [] }],
    rotation_ms: DEFAULT_ROTATION_MS,
  },
  competitionId: null,
  activeSlideIndex: 0,
  selectedWidgetId: null,
  scaleFactor: 1,
  nextId: 2,
})

export function setCompetitionId(competitionId: number | null) {
  DashboardEditorStore.update((s) => {
    s.competitionId = competitionId
  })
}

function nextWidgetId(): string {
  const state = DashboardEditorStore.getRawState()
  const id = `w${state.nextId}`
  DashboardEditorStore.update((s) => {
    s.nextId += 1
  })
  return id
}

function nextSlideId(): string {
  const state = DashboardEditorStore.getRawState()
  const id = `s${state.nextId}`
  DashboardEditorStore.update((s) => {
    s.nextId += 1
  })
  return id
}

export function selectSlide(index: number) {
  DashboardEditorStore.update((s) => {
    s.activeSlideIndex = index
    s.selectedWidgetId = null
  })
}

export function addSlide() {
  const id = nextSlideId()
  DashboardEditorStore.update((s) => {
    s.dashboard.slides.push({ id, widgets: [] })
    s.activeSlideIndex = s.dashboard.slides.length - 1
    s.selectedWidgetId = null
  })
}

export function updateSlideTitle(index: number, title: string) {
  DashboardEditorStore.update((s) => {
    const slide = s.dashboard.slides[index]
    if (slide) slide.title = title
  })
}

export function deleteSlide(index: number) {
  DashboardEditorStore.update((s) => {
    if (s.dashboard.slides.length <= 1) return
    s.dashboard.slides.splice(index, 1)
    if (s.activeSlideIndex >= s.dashboard.slides.length) {
      s.activeSlideIndex = s.dashboard.slides.length - 1
    }
    s.selectedWidgetId = null
  })
}

export function updateScaleFactor(containerWidth: number) {
  DashboardEditorStore.update((s) => {
    s.scaleFactor = containerWidth / REFERENCE_WIDTH
  })
}

export function selectWidget(widgetId: string | null) {
  DashboardEditorStore.update((s) => {
    s.selectedWidgetId = widgetId
  })
}

export function addWidget(type: WidgetType): boolean {
  const def = getDashboardWidget(type)
  if (!def) return false
  const state = DashboardEditorStore.getRawState()
  const slide = state.dashboard.slides[state.activeSlideIndex]

  const pos = findEmptySpot(slide.widgets, def.minW, def.minH)
  if (!pos) return false

  const id = nextWidgetId()
  DashboardEditorStore.update((s) => {
    s.dashboard.slides[s.activeSlideIndex].widgets.push({
      id,
      type,
      position: {
        col: pos.x,
        row: pos.y,
        colSpan: def.minW,
        rowSpan: def.minH,
      },
      settings: structuredClone(def.defaultSettings) as Record<string, unknown>,
    })
    s.selectedWidgetId = id
  })
  return true
}

export function removeWidget(widgetId: string) {
  DashboardEditorStore.update((s) => {
    const slide = s.dashboard.slides[s.activeSlideIndex]
    slide.widgets = slide.widgets.filter((w) => w.id !== widgetId)
    if (s.selectedWidgetId === widgetId) {
      s.selectedWidgetId = null
    }
  })
}

export function updateWidgetPosition(
  widgetId: string,
  col: number,
  row: number,
  colSpan: number,
  rowSpan: number
) {
  DashboardEditorStore.update((s) => {
    const slide = s.dashboard.slides[s.activeSlideIndex]
    const widget = slide.widgets.find((w) => w.id === widgetId)
    if (widget) {
      widget.position = { col, row, colSpan, rowSpan }
    }
  })
}

export function updateWidgetSettings(
  widgetId: string,
  patch: Record<string, unknown>
) {
  DashboardEditorStore.update((s) => {
    for (const slide of s.dashboard.slides) {
      const widget = slide.widgets.find((w) => w.id === widgetId)
      if (widget) {
        widget.settings = { ...widget.settings, ...patch }
        return
      }
    }
  })
}

export function serializeDashboard(): Dashboard {
  return DashboardEditorStore.getRawState().dashboard
}

/**
 * Reset the editor to a single empty slide. Used when opening a dashboard that
 * has no saved config yet (e.g. a freshly created one) so the shared editor
 * store doesn't carry over the previously opened dashboard's widgets.
 */
export function resetDashboard() {
  DashboardEditorStore.update((s) => {
    s.dashboard = {
      slides: [{ id: 's1', widgets: [] }],
      rotation_ms: DEFAULT_ROTATION_MS,
    }
    s.activeSlideIndex = 0
    s.selectedWidgetId = null
    s.nextId = 2
  })
}

export function loadDashboard(config: unknown) {
  // An empty or invalid config (e.g. a freshly created dashboard with no saved
  // slides) must clear the shared store, not leave it holding the previously
  // opened dashboard's widgets.
  if (!config || typeof config !== 'object') {
    resetDashboard()
    return
  }

  const data = config as {
    slides?: unknown[]
    rotation_ms?: number
  }
  if (!Array.isArray(data.slides) || data.slides.length === 0) {
    resetDashboard()
    return
  }

  let droppedWidgets = 0
  const droppedTypes = new Set<string>()

  const slides: Slide[] = data.slides.map((rawSlide, slideIdx) => {
    const s = (rawSlide || {}) as {
      id?: string
      title?: unknown
      widgets?: unknown[]
    }
    const widgets: WidgetInstance[] = Array.isArray(s.widgets)
      ? s.widgets
          .map((rawWidget): WidgetInstance | null => {
            const w = (rawWidget || {}) as Partial<WidgetInstance> & {
              type?: string
            }
            if (!w.id || !w.type || !w.position) return null
            if (!hasDashboardWidget(w.type)) {
              droppedWidgets += 1
              droppedTypes.add(w.type)
              return null
            }
            const def = getDashboardWidget(w.type)!
            const parsed = def.schema.safeParse(w.settings ?? {})
            const settings = parsed.success
              ? (parsed.data as Record<string, unknown>)
              : (structuredClone(def.defaultSettings) as Record<
                  string,
                  unknown
                >)
            if (!parsed.success) {
              notifications.show({
                title: 'Invalid widget settings',
                message: `Widget ${w.id} (${w.type}) had invalid settings; using defaults.`,
                color: 'yellow',
              })
            }
            return {
              id: w.id,
              type: w.type,
              position: w.position,
              settings,
            }
          })
          .filter((w): w is WidgetInstance => w != null)
      : []
    return {
      id: s.id || `s${slideIdx + 1}`,
      title: typeof s.title === 'string' ? s.title : undefined,
      widgets,
    }
  })

  if (droppedWidgets > 0) {
    notifications.show({
      title: 'Unknown widget types',
      message: `Dropped ${droppedWidgets} widget(s) of unknown type: ${Array.from(droppedTypes).join(', ')}`,
      color: 'yellow',
    })
  }

  // Compute next id past anything we loaded
  let maxNum = 0
  for (const slide of slides) {
    const n = parseInt(slide.id.replace(/^s/, ''), 10)
    if (!Number.isNaN(n)) maxNum = Math.max(maxNum, n)
    for (const w of slide.widgets) {
      const m = parseInt(w.id.replace(/^w/, ''), 10)
      if (!Number.isNaN(m)) maxNum = Math.max(maxNum, m)
    }
  }

  DashboardEditorStore.update((s) => {
    s.dashboard = {
      slides,
      rotation_ms: data.rotation_ms ?? DEFAULT_ROTATION_MS,
    }
    s.activeSlideIndex = 0
    s.selectedWidgetId = null
    s.nextId = maxNum + 1
  })
}

// Find first empty spot that fits the given dimensions
function findEmptySpot(
  widgets: {
    position: { col: number; row: number; colSpan: number; rowSpan: number }
  }[],
  w: number,
  h: number
): { x: number; y: number } | null {
  const occupied = Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => false)
  )

  for (const widget of widgets) {
    const { col, row, colSpan, rowSpan } = widget.position
    for (let r = row; r < row + rowSpan && r < GRID_ROWS; r++) {
      for (let c = col; c < col + colSpan && c < GRID_COLS; c++) {
        occupied[r][c] = true
      }
    }
  }

  for (let r = 0; r <= GRID_ROWS - h; r++) {
    for (let c = 0; c <= GRID_COLS - w; c++) {
      let fits = true
      for (let dr = 0; dr < h && fits; dr++) {
        for (let dc = 0; dc < w && fits; dc++) {
          if (occupied[r + dr][c + dc]) fits = false
        }
      }
      if (fits) return { x: c, y: r }
    }
  }

  return null
}
